// Habillage sonore synthétisé (aucun fichier) : musique d'ambiance du menu et de la planque, ambiance de raid,
// couches des tirs (corps grave et écho extérieur), sons d'interface et petites ponctuations (niveau, extraction, mort).
// Tout passe par les bus de Audio3D (musique et ambiance dans « ambiance », le reste dans « effets »).
import type { Audio3D } from './audio';

type Mode = 'off' | 'menu' | 'hideout' | 'raid';
const A_MINOR = [57, 60, 62, 64, 67, 69, 72, 74, 76]; // la mineur pentatonique (MIDI)
// Accords lents (MIDI) : la mineur, fa, ré mineur, mi majeur (la tension qui revient au la)
const CHORDS = [[45, 57, 60, 64], [41, 57, 60, 65], [38, 57, 62, 65], [40, 56, 59, 64]];
const hz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

export class Soundscape {
  private mode: Mode = 'off';
  private music: GainNode | null = null; // musique (menu, planque)
  private raid: GainNode | null = null; // ambiance de raid
  private verb: ConvolverNode | null = null;
  private echo: GainNode | null = null; // écho des tirs
  private timer = 0;
  private nextChord = 0;
  private chordIx = 0;
  private nextPluck = 0;
  private nextBoom = 0;
  private nextStatic = 0;
  private nextCricket = 0;
  private nextDistant = 0;
  private drone: OscillatorNode[] = [];
  private noise: AudioBuffer | null = null;
  private lastHover = 0;
  private heartAt = 0;

  constructor(private a: Audio3D) {}

  private get ctx() { return this.a.ctx!; }

  // Mise en place des bus (une fois le contexte audio créé)
  private setup() {
    if (this.music || !this.a.ctx) return;
    const ctx = this.ctx;
    // Bruit blanc réutilisé (vent, souffle, craquements)
    this.noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    // Réverbération : réponse impulsionnelle stéréo générée (queue de 3 s)
    this.verb = ctx.createConvolver();
    const len = ctx.sampleRate * 3, ir = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) { const ch = ir.getChannelData(c); for (let i = 0; i < len; i++) ch[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2); }
    this.verb.buffer = ir;
    const verbOut = ctx.createGain(); verbOut.gain.value = 0.55;
    this.verb.connect(verbOut).connect(this.a.bus('amb'));
    this.music = ctx.createGain(); this.music.gain.value = 0;
    this.music.connect(this.a.bus('music'));
    this.raid = ctx.createGain(); this.raid.gain.value = 0;
    this.raid.connect(this.a.bus('amb'));
    // Écho extérieur des tirs : deux réflexions filtrées (bâtiments, lisière)
    this.echo = ctx.createGain(); this.echo.gain.value = 1;
    for (const [t, g] of [[0.16, 0.32], [0.41, 0.18]] as const) {
      const dl = ctx.createDelay(1); dl.delayTime.value = t;
      const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 1400;
      const gg = ctx.createGain(); gg.gain.value = g;
      this.echo.connect(dl).connect(lp).connect(gg).connect(this.a.bus('sfx'));
    }
    this.echo.connect(this.verb);
    this.timer = window.setInterval(() => this.tick(), 120);
  }

  // Pluie : bruit filtré en boucle ; la nuit, un coup de tonnerre de temps en temps
  private rainSrc: AudioBufferSourceNode | null = null;
  private rainGain: GainNode | null = null;
  private storm = false;
  private nextThunder = 0;
  setWeather(rain: boolean, night: boolean) {
    this.setup();
    if (!this.a.ctx || !this.noise) return;
    const ctx = this.ctx, t = ctx.currentTime;
    if (rain && !this.rainSrc) {
      const src = ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
      const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1600; bp.Q.value = 0.5;
      const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 400;
      this.rainGain = ctx.createGain(); this.rainGain.gain.value = 0;
      src.connect(bp).connect(hp).connect(this.rainGain).connect(this.a.bus('amb'));
      src.start();
      this.rainSrc = src;
    }
    this.rainGain?.gain.setTargetAtTime(rain ? 0.22 : 0, t, 1.5);
    this.storm = rain && night;
    this.nextThunder = t + 8 + Math.random() * 20;
  }

  // Changer d'ambiance en fondu : menu (thème complet), planque (plus calme), raid (sans musique), coupé
  setMode(m: Mode) {
    this.setup();
    if (!this.music || m === this.mode) return;
    const t = this.ctx.currentTime;
    this.mode = m;
    this.music.gain.setTargetAtTime(m === 'menu' ? 0.5 : m === 'hideout' ? 0.28 : 0, t, 1.2);
    this.raid!.gain.setTargetAtTime(m === 'raid' ? 1 : 0, t, 1.5);
    if ((m === 'menu' || m === 'hideout') && !this.drone.length) this.startDrone();
    this.nextChord = this.nextPluck = t;
    this.nextBoom = t + 12; this.nextStatic = t + 4; this.nextCricket = t + 2; this.nextDistant = t + 15;
  }

  // ---------------------------------------------------------------- Musique

  // Bourdon grave permanent : la grave et quinte, filtrés, qui respirent lentement
  private startDrone() {
    const ctx = this.ctx;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 220; lp.Q.value = 0.7;
    const g = ctx.createGain(); g.gain.value = 0.22;
    const lfo = ctx.createOscillator(); lfo.frequency.value = 0.05;
    const lfoG = ctx.createGain(); lfoG.gain.value = 0.08;
    lfo.connect(lfoG).connect(g.gain);
    for (const [f, type] of [[hz(33), 'sawtooth'], [hz(33) * 1.004, 'sawtooth'], [hz(40), 'sine']] as const) {
      const o = ctx.createOscillator(); o.type = type; o.frequency.value = f;
      o.connect(lp); o.start(); this.drone.push(o);
    }
    lp.connect(g).connect(this.music!);
    lfo.start(); this.drone.push(lfo);
  }

  // Nappe : chaque note de l'accord en deux dents de scie désaccordées, filtrées, attaque et relâche lentes
  private pad(notes: number[], t: number, dur: number, vol: number) {
    const ctx = this.ctx;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 520; lp.Q.value = 1.2;
    lp.frequency.setValueAtTime(380, t); lp.frequency.linearRampToValueAtTime(900, t + dur * 0.5); lp.frequency.linearRampToValueAtTime(420, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vol, t + dur * 0.35); g.gain.setValueAtTime(vol, t + dur * 0.6); g.gain.linearRampToValueAtTime(0, t + dur + 1.5);
    lp.connect(g);
    g.connect(this.music!);
    const send = ctx.createGain(); send.gain.value = 0.6; g.connect(send).connect(this.verb!);
    for (const n of notes) for (const det of [-6, 6]) {
      const o = ctx.createOscillator(); o.type = 'sawtooth'; o.frequency.value = hz(n); o.detune.value = det;
      const og = ctx.createGain(); og.gain.value = n < 50 ? 0.5 : 0.22;
      o.connect(og).connect(lp); o.start(t); o.stop(t + dur + 2);
    }
  }

  // Note isolée façon piano lointain : sinus + triangle, attaque sèche, longue extinction dans la réverbération
  private pluck(m: number, t: number, vol = 0.12) {
    const ctx = this.ctx;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vol, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0008, t + 3.2);
    for (const [mul, type, v] of [[1, 'sine', 1], [2, 'triangle', 0.25], [3, 'sine', 0.08]] as const) {
      const o = ctx.createOscillator(); o.type = type; o.frequency.value = hz(m) * mul;
      const og = ctx.createGain(); og.gain.value = v;
      o.connect(og).connect(g); o.start(t); o.stop(t + 3.3);
    }
    g.connect(this.music!);
    const send = ctx.createGain(); send.gain.value = 0.9; g.connect(send).connect(this.verb!);
  }

  // Grondement lointain (artillerie, effondrement) : sinus très grave et souffle filtré
  private boom(t: number, vol = 0.35, out: AudioNode = this.music!) {
    const ctx = this.ctx;
    const o = ctx.createOscillator(); o.type = 'sine';
    o.frequency.setValueAtTime(70, t); o.frequency.exponentialRampToValueAtTime(32, t + 1.6);
    const g = ctx.createGain(); g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vol, t + 0.05); g.gain.exponentialRampToValueAtTime(0.001, t + 2.4);
    o.connect(g).connect(out); o.start(t); o.stop(t + 2.5);
    this.noiseBurst(t, 1.8, 260, vol * 0.6, out, 'lowpass');
  }

  private noiseBurst(t: number, dur: number, freq: number, vol: number, out: AudioNode, type: BiquadFilterType = 'bandpass', q = 0.8) {
    const ctx = this.ctx;
    const src = ctx.createBufferSource(); src.buffer = this.noise;
    const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = freq; f.Q.value = q;
    const g = ctx.createGain(); g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0005, t + dur);
    src.connect(f).connect(g).connect(out);
    src.start(t, Math.random()); src.stop(t + dur + 0.05);
  }

  // Grésillement de radio : craquements filtrés, très bas
  private radioStatic(t: number) {
    for (let i = 0; i < 6; i++) this.noiseBurst(t + i * (0.05 + Math.random() * 0.12), 0.05 + Math.random() * 0.1, 1800 + Math.random() * 1500, 0.05, this.music!, 'bandpass', 3);
  }

  // ---------------------------------------------------------------- Ambiance de raid

  // Grillon : salves de stridulations aiguës
  private cricket(t: number) {
    const ctx = this.ctx;
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = 4300 + Math.random() * 500;
    const g = ctx.createGain(); g.gain.value = 0;
    const pan = ctx.createStereoPanner(); pan.pan.value = Math.random() * 2 - 1;
    o.connect(g).connect(pan).connect(this.raid!);
    const n = 3 + Math.floor(Math.random() * 4);
    for (let i = 0; i < n; i++) { const s = t + i * 0.09; g.gain.setValueAtTime(0, s); g.gain.linearRampToValueAtTime(0.018, s + 0.012); g.gain.linearRampToValueAtTime(0, s + 0.05); }
    o.start(t); o.stop(t + n * 0.09 + 0.1);
  }

  private tick() {
    if (!this.a.ctx || this.mode === 'off') return;
    const t = this.ctx.currentTime, ahead = t + 0.4;
    if (this.mode === 'menu' || this.mode === 'hideout') {
      if (this.nextChord < ahead) {
        const dur = 9;
        this.pad(CHORDS[this.chordIx % CHORDS.length]!, this.nextChord, dur, this.mode === 'menu' ? 0.09 : 0.06);
        this.chordIx++; this.nextChord += dur;
      }
      if (this.nextPluck < ahead) {
        if (Math.random() < (this.mode === 'menu' ? 0.7 : 0.4)) this.pluck(A_MINOR[Math.floor(Math.random() * A_MINOR.length)]! + (Math.random() < 0.3 ? 12 : 0), this.nextPluck);
        this.nextPluck += 1.6 + Math.random() * 2.8;
      }
      if (this.mode === 'menu' && this.nextBoom < ahead) { this.boom(this.nextBoom); this.nextBoom += 22 + Math.random() * 20; }
      if (this.nextStatic < ahead) { if (Math.random() < 0.5) this.radioStatic(this.nextStatic); this.nextStatic += 9 + Math.random() * 12; }
    } else if (this.mode === 'raid') {
      if (this.nextCricket < ahead) { if (!this.rainGain || this.rainGain.gain.value < 0.05) this.cricket(this.nextCricket); this.nextCricket += 0.4 + Math.random() * 1.6; }
      if (this.storm && this.nextThunder < ahead) { this.boom(this.nextThunder, 0.55, this.raid!); this.nextThunder += 25 + Math.random() * 35; }
      // Échos de la zone : tir lointain ou hurlement, d'un côté au hasard
      if (this.nextDistant < ahead) {
        const pan = this.ctx.createStereoPanner(); pan.pan.value = Math.random() * 1.6 - 0.8; pan.connect(this.raid!);
        if (Math.random() < 0.6) this.a.playTo(['shot_ar', 'shot_dmr', 'shot_pistol', 'shot_sg'][Math.floor(Math.random() * 4)]!, pan, { vol: 0.16, lowpass: 700, rate: 0.85 });
        else this.a.playTo(this.a.pick('growl', 4), pan, { vol: 0.12, lowpass: 900, rate: 0.7 });
        this.nextDistant += 18 + Math.random() * 30;
      }
    }
  }

  // ---------------------------------------------------------------- Tirs

  // Couches ajoutées au son enregistré : corps grave (souffle), claquement, écho extérieur
  private static SHOT: Record<string, { body: number; crack: number; echo: number; rate: number }> = {
    pistol: { body: 0.35, crack: 0.25, echo: 0.35, rate: 1.08 },
    ar: { body: 0.55, crack: 0.45, echo: 0.55, rate: 1.0 },
    dmr: { body: 0.75, crack: 0.6, echo: 0.8, rate: 0.86 },
    sg: { body: 0.9, crack: 0.3, echo: 0.65, rate: 0.92 },
    smg: { body: 0.35, crack: 0.3, echo: 0.4, rate: 1.15 },
    ak: { body: 0.7, crack: 0.5, echo: 0.65, rate: 0.93 },
    bolt: { body: 0.85, crack: 0.7, echo: 0.9, rate: 0.8 },
  };
  // sup : arme silencieuse (son étouffé, sans claquement ni écho, porte moins loin)
  shot(w: string, x?: number, z?: number, dist = 0, sup = false) {
    this.setup();
    const p = Soundscape.SHOT[w] ?? Soundscape.SHOT.ar!;
    const far = Math.min(1, dist / (sup ? 30 : 80));
    this.a.play(`shot_${w}`, { x, z, vol: sup ? 0.45 : 1.2, ref: sup ? 3 : 6, max: sup ? 50 : 150, lowpass: sup ? 900 : far > 0.5 ? 2500 : undefined, rate: p.rate * (sup ? 1.15 : 1) });
    if (!this.echo || sup) return;
    const ctx = this.ctx, t = ctx.currentTime;
    const near = 1 - far;
    // Corps : coup de pression grave
    const o = ctx.createOscillator(); o.type = 'sine';
    o.frequency.setValueAtTime(150, t); o.frequency.exponentialRampToValueAtTime(40, t + 0.22);
    const g = ctx.createGain(); g.gain.setValueAtTime(p.body * near * 0.9, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    o.connect(g).connect(this.a.bus('sfx')); o.start(t); o.stop(t + 0.32);
    // Claquement sec (balle supersonique) et écho vers les réflexions
    if (near > 0.2) this.noiseBurst(t, 0.06, 3500, p.crack * near * 0.5, this.a.bus('sfx'), 'highpass', 0.7);
    const eg = ctx.createGain(); eg.gain.value = p.echo * (0.35 + far * 0.4);
    this.noiseBurst(t, 0.12, 900, 0.6, eg, 'lowpass');
    eg.connect(this.echo);
  }

  // ---------------------------------------------------------------- Interface et ponctuations

  private toneOut: AudioNode | null = null; // sortie des notes jouées pour l'interface
  private tone(f: number, t: number, dur: number, vol: number, type: OscillatorType = 'sine', out: AudioNode = this.toneOut ?? this.a.bus('sfx')) {
    const ctx = this.ctx;
    const o = ctx.createOscillator(); o.type = type; o.frequency.value = f;
    const g = ctx.createGain(); g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vol, t + 0.005); g.gain.exponentialRampToValueAtTime(0.0005, t + dur);
    o.connect(g).connect(out); o.start(t); o.stop(t + dur + 0.02);
  }

  ui(kind: 'click' | 'hover' | 'tab' | 'buy' | 'sell' | 'error' | 'deploy' | 'craft' | 'notify' | 'equip') {
    this.setup();
    if (!this.a.ctx) return;
    const t = this.ctx.currentTime, out = this.a.bus('ui');
    this.toneOut = out;
    switch (kind) {
      case 'hover': if (performance.now() - this.lastHover < 60) return; this.lastHover = performance.now(); this.tone(2600, t, 0.025, 0.02); break;
      case 'click': this.tone(1700, t, 0.04, 0.05, 'triangle'); this.noiseBurst(t, 0.03, 4000, 0.04, out, 'highpass'); break;
      case 'tab': this.noiseBurst(t, 0.14, 1200, 0.06, out, 'bandpass', 1.5); this.tone(900, t, 0.06, 0.03, 'triangle'); break;
      case 'buy': this.tone(1320, t, 0.18, 0.06); this.tone(1980, t + 0.06, 0.25, 0.05); this.noiseBurst(t, 0.05, 6000, 0.05, out, 'highpass'); break;
      case 'sell': this.tone(1760, t, 0.15, 0.05); this.tone(1320, t + 0.07, 0.22, 0.05); break;
      case 'error': this.tone(150, t, 0.12, 0.08, 'square'); this.tone(140, t + 0.14, 0.14, 0.08, 'square'); break;
      case 'deploy': this.radioStatic(t); this.boom(t + 0.15, 0.3, out); this.tone(660, t, 0.08, 0.04, 'square'); break;
      case 'craft': for (let i = 0; i < 4; i++) this.noiseBurst(t + i * 0.06, 0.03, 2500, 0.06, out, 'bandpass', 4); break;
      case 'notify': this.tone(988, t, 0.12, 0.04); this.tone(1318, t + 0.08, 0.18, 0.04); break;
      case 'equip': this.a.play(this.a.pick('cloth', 2), { vol: 0.6 }); break;
    }
    this.toneOut = null;
  }

  // Bip d'extraction (chaque seconde) et battements de cœur quand la santé est basse
  extractBeep(left: number) { this.setup(); if (this.a.ctx) this.tone(left <= 3 ? 1320 : 990, this.ctx.currentTime, 0.09, 0.07, 'square'); }
  heartbeat(hp: number) {
    if (!this.a.ctx || hp > 30 || hp <= 0) return;
    const now = performance.now();
    if (now < this.heartAt) return;
    this.heartAt = now + 700 + hp * 12;
    const t = this.ctx.currentTime;
    for (const [dt, v] of [[0, 0.22], [0.16, 0.15]] as const) {
      const o = this.ctx.createOscillator(); o.frequency.setValueAtTime(70, t + dt); o.frequency.exponentialRampToValueAtTime(40, t + dt + 0.12);
      const g = this.ctx.createGain(); g.gain.setValueAtTime(v, t + dt); g.gain.exponentialRampToValueAtTime(0.001, t + dt + 0.16);
      o.connect(g).connect(this.a.bus('sfx')); o.start(t + dt); o.stop(t + dt + 0.18);
    }
  }

  // Ponctuations : niveau supérieur (arpège majeur), extraction réussie (accord qui s'ouvre), mort (grondement dissonant)
  sting(kind: 'level' | 'extract' | 'death') {
    this.setup();
    if (!this.a.ctx) return;
    const t = this.ctx.currentTime, out = this.a.bus('music');
    if (kind === 'level') [69, 73, 76, 81].forEach((m, i) => this.tone(hz(m), t + i * 0.11, 0.9, 0.07, 'triangle', out));
    else if (kind === 'extract') { for (const m of [57, 64, 69, 73]) this.tone(hz(m), t, 2.6, 0.05, 'triangle', out); this.pluck(81, t + 0.3, 0.1); }
    else { this.boom(t, 0.5, out); for (const m of [40, 41, 46]) this.tone(hz(m), t, 3, 0.06, 'sawtooth', out); }
  }
}
